import { useNavigate } from "react-router-dom";
import {
  Pill, Car, HeartHandshake, Stethoscope, UtensilsCrossed,
  ShoppingBasket, Utensils, ArrowRight, CheckCircle2, Zap, Users,
  IndianRupee, Shield, Mail, Bell
} from "lucide-react";

const CATEGORIES = [
  { id: "medicine_delivery", label: "Medicine Delivery", desc: "Deliver prescribed medicines to senior citizens at their doorstep.", icon: Pill, tone: "#F59E0B" },
  { id: "nurse_service", label: "Home Nurse Service", desc: "Provide attendant & nursing care for elderly patients at home.", icon: HeartHandshake, tone: "#EF4444" },
  { id: "doctor_appointment", label: "Doctor Appointment", desc: "Schedule teleconsultations or in-clinic visits with specialists.", icon: Stethoscope, tone: "#3B82F6" },
  { id: "cab_ride", label: "Cab / Ride", desc: "Senior-friendly cab and chauffeur services across the city.", icon: Car, tone: "#8B5CF6" },
  { id: "food_delivery", label: "Food Delivery", desc: "Home-cooked meals and restaurant food delivered fresh.", icon: UtensilsCrossed, tone: "#10B981" },
  { id: "grocery_delivery", label: "Grocery Delivery", desc: "Daily essentials, kirana, and grocery delivered weekly or on demand.", icon: ShoppingBasket, tone: "#14B8A6" },
  { id: "restaurant_booking", label: "Restaurant Booking", desc: "Partner with Dine-Out and handle senior-friendly table reservations.", icon: Utensils, tone: "#EC4899" },
];

const BENEFITS = [
  { icon: Bell, title: "Instant AI-matched leads", desc: "Our AI routes service requests from seniors directly to your inbox based on your category." },
  { icon: IndianRupee, title: "Transparent commission", desc: "Flat per-category commission, shown upfront. Keep every rupee of what you earn on top." },
  { icon: Mail, title: "Email notifications", desc: "Never miss a lead — we email you the moment a matching request comes in." },
  { icon: Shield, title: "Verified & trusted", desc: "Verify your email, get listed, and build ratings that bring repeat customers." },
];

export default function VendorLanding() {
  const navigate = useNavigate();

  const handleJoin = () => navigate("/vendor");

  return (
    <div className="min-h-screen bg-white" data-testid="vendor-landing">
      {/* Navbar */}
      <header className="sticky top-0 z-40 border-b bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#2D2766" }}>
              <HeartHandshake className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <p className="font-bold text-[#2D2766] text-base">Elderra Partner</p>
              <p className="text-[10px] text-gray-500 -mt-0.5">Serving India's seniors</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/vendor")}
              className="hidden sm:inline-flex text-sm font-medium text-[#2D2766] hover:underline px-3"
              data-testid="landing-signin-btn"
            >
              Sign in
            </button>
            <button
              onClick={handleJoin}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all active:scale-95"
              style={{ backgroundColor: "#E8B830" }}
              data-testid="landing-join-btn-nav"
            >
              Join Now <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2D2766 0%, #453c8a 55%, #6b5fb8 100%)" }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #fff 2px, transparent 3px), radial-gradient(circle at 80% 40%, #fff 2px, transparent 3px)",
                      backgroundSize: "140px 140px" }} />
        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-medium mb-5">
              <Zap className="w-3.5 h-3.5 text-[#E8B830]" /> 0% onboarding fee • Pay only when you earn
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Become a trusted <span style={{ color: "#E8B830" }}>Elderra Partner</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
              Reach thousands of senior citizens across India who need medicine, cabs, nurses, groceries and more — every day. Join the network that puts our elders first.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={handleJoin}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-[#2D2766] text-base font-bold transition-all active:scale-95 shadow-lg"
                style={{ backgroundColor: "#E8B830" }}
                data-testid="landing-hero-cta"
              >
                Register Your Business <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#categories"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/30 text-white text-base font-semibold hover:bg-white/10 transition-all"
                data-testid="landing-hero-categories"
              >
                See Categories
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 text-white/80 text-sm">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#E8B830]" /> 10 Indian languages</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#E8B830]" /> AI-routed leads</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#E8B830]" /> Instant payouts</div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="py-16 md:py-20 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E8B830] mb-2">What you can offer</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D2766] leading-tight">Seven service categories. One trusted network.</h2>
            <p className="mt-3 text-gray-600 text-base">Pick any combination during signup. We'll match you with relevant senior requests in real time.</p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.id}
                  className="group relative bg-white border border-gray-200 rounded-2xl p-6 transition-all hover:-translate-y-1 hover:shadow-xl hover:border-[#2D2766]/30"
                  data-testid={`category-${cat.id}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${cat.tone}15` }}>
                    <Icon className="w-6 h-6" style={{ color: cat.tone }} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg">{cat.label}</h3>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{cat.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E8B830] mb-2">How it works</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D2766]">Start earning in 3 simple steps</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="steps-grid">
            {[
              { n: "01", t: "Sign up", d: "Create your partner account in minutes. Pick the service categories you offer." },
              { n: "02", t: "Verify email", d: "Click the link we send you. Verified partners can instantly start accepting leads." },
              { n: "03", t: "Accept & deliver", d: "Get AI-matched requests, quote your price, deliver the service, and collect the payout." },
            ].map((s) => (
              <div key={s.n} className="relative bg-white border border-gray-100 rounded-2xl p-6 shadow-sm" data-testid={`step-${s.n}`}>
                <div className="absolute -top-4 left-6 text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: "#2D2766" }}>{s.n}</div>
                <h3 className="font-bold text-lg text-gray-900 mt-3">{s.t}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E8B830] mb-2">Why Elderra</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2D2766] leading-tight">Built for partners who care.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4" data-testid="benefits-grid">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="flex gap-4 p-5 bg-white border border-gray-100 rounded-2xl">
                  <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#2D276615" }}>
                    <Icon className="w-5 h-5 text-[#2D2766]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-base">{b.title}</h3>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16" style={{ background: "linear-gradient(135deg, #2D2766 0%, #4a3f99 100%)" }}>
        <div className="max-w-5xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-testid="stats-strip">
          {[
            { v: "10+", l: "Indian languages" },
            { v: "7", l: "Service categories" },
            { v: "24/7", l: "AI-matched leads" },
            { v: "0%", l: "Onboarding fee" },
          ].map((s) => (
            <div key={s.l}>
              <p className="text-4xl md:text-5xl font-bold" style={{ color: "#E8B830" }}>{s.v}</p>
              <p className="text-white/70 text-sm mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <Users className="w-12 h-12 mx-auto text-[#2D2766]" />
          <h2 className="mt-4 text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            Ready to serve India's seniors?
          </h2>
          <p className="mt-3 text-gray-600 text-base">
            Join hundreds of trusted partners delivering medicines, rides, groceries and care to those who matter most.
          </p>
          <button
            onClick={handleJoin}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white text-base font-bold transition-all active:scale-95 shadow-lg"
            style={{ backgroundColor: "#2D2766" }}
            data-testid="landing-final-cta"
          >
            Register as Partner <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white py-6">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} Elderra Agent. All rights reserved.</p>
          <div className="flex gap-5">
            <button onClick={() => navigate("/")} className="hover:text-[#2D2766]">For seniors</button>
            <button onClick={() => navigate("/vendor")} className="hover:text-[#2D2766]">Partner login</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
